import React, { useMemo } from "react";
import { Alert, Box, Grid, Paper, Typography } from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PropuestaPagoMedios from "./PropuestaPagoMedios";
import ResumenSeleccion from "./ResumenSeleccion";
import VistaOrdenPagoDocumento from "./VistaOrdenPagoDocumento";
import CertificadoRetencionCard from "./CertificadoRetencionCard";
import {
  excedenteRetencionPreview,
  formatCuit,
  formatMoneda,
  listarCertificadosRetencion,
  obtenerResumenPreview,
  requiereMediosPago,
} from "./utils";

const TabConfirmacion = ({
  comprobantesTabla,
  preview,
  previewLoading,
  previewError,
  previewMediosLote = null,
  previewMediosLoading = false,
  previewMediosError = "",
  medioPago,
  cuentasOrigenDetalle = [],
  proveedores = [],
  configPagoPorProveedor = null,
  onConfigPagoChange,
  cuentasBancarias = [],
  loadingCuentas = false,
  proveedorConError = null,
}) => {
  const certificados = useMemo(
    () => listarCertificadosRetencion(preview),
    [preview]
  );

  const certificadosConRetencion = certificados.filter(
    (c) => c.aplica && Number(c.importe) > 0
  );

  const resumen = obtenerResumenPreview(preview);
  const excedenteRetencion = excedenteRetencionPreview(preview);
  const sinImporteNeto =
    resumen?.total_a_pagar != null && !requiereMediosPago(resumen.total_a_pagar);
  const mostrarPropuesta =
    configPagoPorProveedor != null &&
    proveedores.length > 0 &&
    Object.keys(configPagoPorProveedor).length > 0;

  return (
    <Grid container spacing={2} alignItems="flex-start">
      <Grid item xs={12} lg={8}>
        {proveedorConError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={700}>
              No se generó ninguna orden de pago
            </Typography>
            <Typography variant="body2">
              Falló{" "}
              <strong>
                {proveedorConError.razon_social || "un proveedor"}
                {proveedorConError.cuit
                  ? ` (${formatCuit(proveedorConError.cuit)})`
                  : ""}
              </strong>
              {proveedorConError.mensaje
                ? `: ${proveedorConError.mensaje}`
                : "."}{" "}
              El lote se crea todo o nada: corregí ese proveedor (o sacalo de la
              selección) y volvé a confirmar.
            </Typography>
          </Alert>
        ) : null}

        {previewMediosLoading ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Validando medios de pago del lote…
          </Alert>
        ) : null}
        {previewMediosError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {previewMediosError}
          </Alert>
        ) : null}
        {previewMediosLote && previewMediosLote.medios_lote_ok === false ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Los medios del lote no están completos o hay cheques inconsistentes.
            Volvé al paso anterior para corregir la asignación por proveedor.
          </Alert>
        ) : null}
        {(previewMediosLote?.cheques_no_disponibles ?? []).length > 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Hay cheques seleccionados que ya no están disponibles en cartera.
          </Alert>
        ) : null}

        <Alert severity="info" sx={{ mb: 2 }} icon={false}>
          <Typography variant="body2">
            Revise la <strong>propuesta de pago</strong>, la{" "}
            <strong>orden de pago</strong> y los{" "}
            <strong>certificados de retención</strong> antes de confirmar. Podés
            cambiar medio o banco por proveedor si hace falta (queda registrado
            para la conciliación).
          </Typography>
        </Alert>

        {excedenteRetencion > 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Las retenciones superan el importe a pagar:{" "}
              <strong>{formatMoneda(excedenteRetencion)}</strong> quedan a favor
              de la empresa. Al confirmar se pedirá una validación extra.
            </Typography>
          </Alert>
        ) : sinImporteNeto ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              No queda importe neto para transferir: la orden se registra como
              ajuste, sin medios de pago.
            </Typography>
          </Alert>
        ) : null}

        {mostrarPropuesta ? (
          <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: "#fff" }}>
            <PropuestaPagoMedios
              proveedores={proveedores}
              configPagoPorProveedor={configPagoPorProveedor}
              onConfigPagoChange={onConfigPagoChange}
              cuentasBancarias={cuentasBancarias}
              loadingCuentas={loadingCuentas}
              editable
              titulo="Propuesta de pago (editable)"
              cuitConError={proveedorConError?.cuit || ""}
            />
          </Paper>
        ) : null}

        <VistaOrdenPagoDocumento
          preview={preview}
          detalle={preview?.detalle}
          comprobantesTabla={comprobantesTabla}
          medioPago={medioPago}
          cuentasOrigenDetalle={cuentasOrigenDetalle}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, mt: 1 }}>
          <AccountBalanceWalletOutlinedIcon color="primary" fontSize="small" />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Certificados de retención
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Un documento por impuesto / provincia (independiente de la orden de
              pago)
            </Typography>
          </Box>
        </Box>

        {certificados.length === 0 ? (
          <Alert severity="info">No hay certificados de retención para esta operación.</Alert>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {(() => {
              const porProveedor = new Map();
              certificados.forEach((cert) => {
                const key = cert.cuit || cert.razon_social || "_";
                if (!porProveedor.has(key)) {
                  porProveedor.set(key, {
                    cuit: cert.cuit,
                    razon_social: cert.razon_social,
                    items: [],
                  });
                }
                porProveedor.get(key).items.push(cert);
              });
              const gruposCert = Array.from(porProveedor.values());
              const multi = gruposCert.length > 1 || Boolean(gruposCert[0]?.cuit);

              return gruposCert.map((grupo) => (
                <Box key={String(grupo.cuit || grupo.razon_social || "cert")}>
                  {multi && (grupo.razon_social || grupo.cuit) ? (
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{ mb: 1 }}
                    >
                      {grupo.razon_social || "Proveedor"}
                      {grupo.cuit ? (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 1 }}
                        >
                          {grupo.cuit}
                        </Typography>
                      ) : null}
                    </Typography>
                  ) : null}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {grupo.items.map((cert) => (
                      <CertificadoRetencionCard key={cert.id} certificado={cert} />
                    ))}
                  </Box>
                </Box>
              ));
            })()}
          </Box>
        )}

        {certificadosConRetencion.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
            Total retenido en certificados:{" "}
            <strong>
              {formatMoneda(
                certificadosConRetencion.reduce(
                  (s, c) => s + Number(c.importe || 0),
                  0
                )
              )}
            </strong>
            {" "}
            (debe coincidir con el resumen de la orden)
          </Typography>
        )}
      </Grid>

      <Grid
        item
        xs={12}
        lg={4}
        sx={{
          position: { lg: "sticky" },
          top: { lg: 88 },
          alignSelf: "flex-start",
        }}
      >
        <ResumenSeleccion
          preview={preview}
          previewLoading={previewLoading}
          previewError={previewError}
          formatMoneda={formatMoneda}
          cantidadSeleccionada={
            resumen?.cantidad_comprobantes ?? comprobantesTabla.length
          }
          filasSeleccionadas={comprobantesTabla}
          cantidadOpsEstimada={1}
        />
      </Grid>
    </Grid>
  );
};

export default TabConfirmacion;
